import { Link } from "react-router-dom";
import { AlertCircle, Mail, Video, Phone, Clock } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";

const CONTENT = {
  en: {
    eyebrow: "FAQ & practical info",
    h1a: "Sensible questions, ",
    h1b: "honest answers.",
    items: [
      { q: "How long is a session, and how often do we meet?", a: "Sessions last 50 minutes, usually at the same time each week. Some people prefer fortnightly; we'll find a rhythm that works and review it as we go." },
      { q: "Is what I say confidential?", a: "Yes. Everything you share is confidential. The exceptions are the serious legal ones — a real risk of serious harm to you or someone else, or a court order — and wherever possible we'd talk it through together first." },
      { q: "What if I need to cancel or reschedule?", a: "If you let me know at least 24 hours in advance by email or phone, there's no charge; cancellations after that are charged at the full session fee. Life happens — I'll always be as flexible as I can." },
      { q: "How do I start?", a: "Book a consultation through the enquiry form. We'll begin with a free 15-minute call to see whether we're a good fit." },
    ],
    practical: [
      { icon: Video, title: "Online sessions", lines: ["A secure video link arrives with your confirmation.", "Join from a private space, anywhere."] },
      { icon: Mail, title: "Contact", lines: ["info@ioannistsiros.com", "+30 698 205 2345"] },
    ],
    crisis: "This practice is not an emergency service. If you are in crisis or need urgent support, please contact your local emergency number, your GP, or a crisis line such as [local crisis line] right away.",
  },
  el: {
    eyebrow: "Συχνές ερωτήσεις & πρακτικά",
    h1a: "Λογικές ερωτήσεις, ",
    h1b: "ειλικρινείς απαντήσεις.",
    items: [
      { q: "Πόσο διαρκεί μια συνεδρία και πόσο συχνά συναντιόμαστε;", a: "Οι συνεδρίες διαρκούν 50 λεπτά, συνήθως την ίδια ώρα κάθε εβδομάδα. Κάποιοι προτιμούν ανά δεύτερη εβδομάδα· θα βρούμε μαζί έναν ρυθμό που σας ταιριάζει και τον επανεξετάζουμε στην πορεία." },
      { q: "Είναι εμπιστευτικό ό,τι λέω;", a: "Ναι. Ό,τι μοιράζεστε είναι εμπιστευτικό. Οι εξαιρέσεις είναι οι σοβαρές νομικές — πραγματικός κίνδυνος σοβαρής βλάβης σε εσάς ή σε άλλον, ή δικαστική εντολή — και όσο είναι δυνατόν, τις συζητάμε πρώτα μαζί." },
      { q: "Τι γίνεται αν χρειαστεί να ακυρώσω;", a: "Αν με ενημερώσετε τουλάχιστον 24 ώρες πριν, με email ή τηλέφωνο, δεν υπάρχει χρέωση· οι ακυρώσεις μετά χρεώνονται με το πλήρες τέλος της συνεδρίας. Θα είμαι όσο πιο ευέλικτος μπορώ." },
      { q: "Πώς ξεκινάω;", a: "Κλείστε ραντεβού μέσω της φόρμας αιτήματος. Θα ξεκινήσουμε με μια δωρεάν κλήση 15 λεπτών για να δούμε αν ταιριάζουμε." },
    ],
    practical: [
      { icon: Video, title: "Συνεδρίες online", lines: ["Ένας ασφαλής σύνδεσμος βίντεο έρχεται με την επιβεβαίωση.", "Συμμετάσχετε από έναν ιδιωτικό χώρο, οπουδήποτε."] },
      { icon: Mail, title: "Επικοινωνία", lines: ["info@ioannistsiros.com", "+30 698 205 2345"] },
    ],
    crisis: "Η πρακτική αυτή δεν είναι υπηρεσία έκτακτης ανάγκης. Αν βρίσκεστε σε κρίση ή χρειάζεστε άμεση υποστήριξη, παρακαλώ επικοινωνήστε άμεσα με το τοπικό νούμερο έκτακτης ανάγκης, τον γιατρό σας ή τη Γραμμή Ψυχοκοινωνικής Υποστήριξης – 10306.",
  },
};

const Faq = () => {
  const { lang } = useLang();
  const c = CONTENT[lang];
  useSeo("faq");

  return (
    <div data-testid="page-faq">
      <section className="container-x pb-24 pt-32 md:pt-40 lg:pt-44">
        <FadeUp>
          <p className="eyebrow mb-6">{c.eyebrow}</p>
          <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.05] tracking-tight text-sage-900 sm:text-6xl">
            {c.h1a}
            <em className="italic text-sage-600">{c.h1b}</em>
          </h1>
        </FadeUp>

        <div className="mt-16 grid gap-16 lg:mt-24 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <Accordion type="single" collapsible className="max-w-3xl">
              {c.items.map((item) => (
                <AccordionItem key={item.q} value={item.q} className="border-sage-600/10">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${item.q.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}
                    className="py-6 text-left font-display text-xl font-light text-sage-900 hover:no-underline"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="max-w-2xl text-base leading-relaxed text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>

          <div className="space-y-6 lg:col-span-5">
            {c.practical.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.06}>
                <div className="grain-card bg-card p-8">
                  <p className="flex items-center gap-3 font-medium text-sage-800">
                    <p.icon className="h-4 w-4 text-clay" strokeWidth={1.75} />
                    {p.title}
                  </p>
                  <div className="mt-3 space-y-1">
                    {p.lines.map((l) => (
                      <p key={l} className="text-sm leading-relaxed text-muted-foreground">
                        {l}
                      </p>
                    ))}
                  </div>
                </div>
              </FadeUp>
            ))}

            {lang === "el" && (
              <FadeUp delay={0.2}>
                <div className="flex items-start gap-3 rounded-2xl border border-clay/40 bg-clay-light/40 p-6" data-testid="crisis-note-el">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-clay-dark" />
                  <p className="text-xs leading-relaxed text-foreground/75">{c.crisis}</p>
                </div>
              </FadeUp>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Faq;
